/**
 * RunActions (Stufe 3, docs/09 §7): EINE Schnittstelle fuer alle Spieler-
 * Mutationen. Zwei Implementierungen:
 *   LocalActions — heutiges Verhalten: pure fn + localStorage (Offline/Fallback)
 *   StdbActions  — src/net/stdbRunStore.ts: gleiche pure fn optimistisch lokal
 *                  + Reducer-Call; Server bleibt Autoritaet (Drift-Waechter)
 * Die UI kennt nur dieses Interface — der spaetere Capybara-Client uebernimmt
 * es unveraendert.
 */
import type { FocusId, RunState, ShopPath } from './types'
import type { RunStateStore } from './persistence'
import type { HexPos } from '../sim/hex'
import type { MergeOption } from './merge'
import { continueAfterResult, pickFocus, startFight, type FightOutput } from './runState'
import { buySlot, choosePath, reroll, takeBonusChamp, toggleLock } from './shop'
import { repairFracture } from './fractures'
import {
  equipWeapon, moveToBench, placeChampion, sellChampion, sellWeapon, unequipWeapon,
} from './roster'
import { executeMerge } from './merge'

export type RerollKind = 'board' | 'ratioWeapons' | 'ratioPassives'

export interface RunActions {
  choosePath(path: ShopPath): boolean
  reroll(kind: RerollKind): boolean
  toggleLock(slot: number): boolean
  buySlot(slot: number): boolean
  takeBonusChamp(): boolean
  pickFocus(focus: FocusId): boolean
  repairFracture(): boolean
  placeChampion(uid: number, hex: HexPos): boolean
  moveToBench(uid: number): boolean
  equipWeapon(champUid: number, weaponUid: number): boolean
  unequipWeapon(champUid: number, weaponUid: number): boolean
  sellChampion(uid: number): boolean
  sellWeapon(uid: number): boolean
  executeMerge(option: MergeOption): boolean
  /** Shop abschliessen + Kampf (shop_confirm). Ergebnis fuers sofortige Playback. */
  startFight(): FightOutput | null
  continueAfterResult(): void
  /** Persistenz-Hook nach Mutationen (Local: store.save; STDB: no-op, Server persistiert). */
  persist(): void
  /** Run verwerfen — ersetzt store.clear() (docs/09 §6). */
  abandonRun(): void
}

/** Stufe-2-Verhalten: direkte Mutation + expliziter persist() durch die UI (A8). */
export class LocalActions implements RunActions {
  constructor(
    private readonly state: RunState,
    private readonly store: RunStateStore,
  ) {}

  choosePath(path: ShopPath): boolean { return choosePath(this.state, path) }
  reroll(kind: RerollKind): boolean { return reroll(this.state, kind) }
  toggleLock(slot: number): boolean { return toggleLock(this.state, slot) }
  buySlot(slot: number): boolean { return buySlot(this.state, slot) }
  takeBonusChamp(): boolean { return takeBonusChamp(this.state) }
  pickFocus(focus: FocusId): boolean { return pickFocus(this.state, focus) }
  repairFracture(): boolean { return repairFracture(this.state) }
  placeChampion(uid: number, hex: HexPos): boolean { return placeChampion(this.state, uid, hex) }
  moveToBench(uid: number): boolean { return moveToBench(this.state, uid) }
  equipWeapon(champUid: number, weaponUid: number): boolean {
    return equipWeapon(this.state, champUid, weaponUid)
  }
  unequipWeapon(champUid: number, weaponUid: number): boolean {
    return unequipWeapon(this.state, champUid, weaponUid)
  }
  sellChampion(uid: number): boolean { return sellChampion(this.state, uid) }
  sellWeapon(uid: number): boolean { return sellWeapon(this.state, uid) }
  executeMerge(option: MergeOption): boolean { return executeMerge(this.state, option) }
  startFight(): FightOutput | null {
    const out = startFight(this.state)
    if (out) this.store.save(this.state)
    return out
  }
  continueAfterResult(): void { continueAfterResult(this.state) }
  persist(): void { this.store.save(this.state) }
  abandonRun(): void { this.store.clear() }
}
